import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { fetchRefundGroup, fetchRefundRequests, fetchRefundRoster, reviewRefund } from "../repo/ticketRefundRepo";
import type { RefundFilters, RefundRow, ReviewRequest } from "../types";

/** 管理端分页与审核状态统一编排。 */
export function useRefundAdmin(teamId: string) {
  const client = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rosterVisible, setRosterVisible] = useState(false);
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterPageSize, setRosterPageSize] = useState(20);
  const roster = useQuery({ queryKey: ["ticket-refund-roster", teamId, rosterPage, rosterPageSize], queryFn: () => fetchRefundRoster(teamId, rosterPage, rosterPageSize), enabled: rosterVisible, retry: false });
  const [draftFilters, setDraftFilters] = useState<RefundFilters>({});
  const [filters, setFilters] = useState<RefundFilters>({});
  const [reviewing, setReviewing] = useState<RefundRow>();
  const [decision, setDecision] = useState<ReviewRequest["decision"]>("approved");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const busy = useRef(false);
  const group = useQuery({ queryKey: ["ticket-refund-group", teamId], queryFn: () => fetchRefundGroup(teamId), retry: false });
  const list = useQuery({ queryKey: ["ticket-refund-list", teamId, page, pageSize, filters], queryFn: () => fetchRefundRequests(teamId, page, pageSize, filters), retry: false });
  const review = useMutation({ mutationFn: ({ id, body }: { id: string; body: ReviewRequest }) => reviewRefund(teamId, id, body) });
  /** 仅提交条件时查询，避免输入中的条件影响翻页。 */
  function applyFilters(next: RefundFilters) {
    setPage(1);
    setFilters(next);
    if (page === 1 && JSON.stringify(next) === JSON.stringify(filters)) void list.refetch();
  }
  /** 清理空白条件并提交本次搜索。 */
  function search() {
    const next: RefundFilters = {};
    for (const key of ["name", "phone", "certificate_no"] as const) {
      const value = draftFilters[key]?.trim();
      if (value) next[key] = value;
    }
    if (draftFilters.status) next.status = draftFilters.status;
    applyFilters(next);
  }
  /** 清空输入及生效条件，恢复本团第一页。 */
  function resetFilters() { setDraftFilters({}); applyFilters({}); }

  /** 每次打开审核清除上一次意见。 */
  function openReview(row: RefundRow) { setReviewing(row); setDecision("approved"); setNote(""); setError(""); }
  /** 提交审核后刷新本团各页，冲突时同步最新状态。 */
  async function confirmReview() {
    if (!reviewing || busy.current) return;
    if (decision === "rejected" && !note.trim()) { setError("标记未退款时请填写失败原因"); return; }
    busy.current = true;
    setError("");
    try {
      await review.mutateAsync({ id: reviewing.id, body: { decision, note: note.trim() } });
      setReviewing(undefined);
    } catch (cause) { setError(resolveReadableHttpErrorMessage(cause, "状态保存失败，请重试")); }
    finally {
      busy.current = false;
      void client.invalidateQueries({ queryKey: ["ticket-refund-list", teamId] });
    }
  }
  return { group, list, page, pageSize, setPage, changePageSize: (size: number) => { setPage(1); setPageSize(size); },
    roster, rosterVisible, setRosterVisible, rosterPage, setRosterPage, rosterPageSize,
    changeRosterPageSize: (size: number) => { setRosterPage(1); setRosterPageSize(size); },
    draftFilters, setDraftFilters, search, resetFilters,
    reviewing, openReview, closeReview: () => { if (!busy.current) setReviewing(undefined); },
    decision, setDecision, note, setNote, error, review, confirmReview };
}
